// src/components/DeviceInfo/DeviceInfo.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const DeviceInfo = () => {
  const [deviceId, setDeviceId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const getDeviceInfo = async () => {
    const requestId = uuidv4();
    const timestamp = new Date().toISOString();
    
    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Validate input
      if (!deviceId.trim()) {
        throw {
          custom: true,
          requestId,
          code: "MISSING_DEVICE_ID",
          message: "Device ID is required",
          action: "validate_input",
          timestamp,
          details: {
            required_fields: ["deviceId"]
          }
        };
      }

      // Get token from environment variables
      const token ="3585535a-137c-499e-82ad-af9feff7d8e7"
      if (!token) {
        throw {
          custom: true,
          requestId,
          code: "MISSING_TOKEN_CONFIG",
          message: "Client configuration error - missing API token",
          action: "verify_configuration",
          timestamp,
          details: {
            solution: "Configure REACT_APP_TOKEN_ID in your .env file"
          }
        };
      }

      // Configure endpoints
      const endpoints=[
        "https://icloud.assetscontrols.com:3443/OpenApi/Admin",
        "http://icloud.assetscontrols.com:8092/OpenApi/Admin"
      ].filter(Boolean);

      if (endpoints.length === 0) {
        throw {
          custom: true,
          requestId,
          code: "MISSING_ENDPOINTS",
          message: "No API endpoints configured",
          action: "verify_configuration",
          timestamp
        };
      }

      const payload = {
        FAction: "QueryAdminAssetByAssetId",
        FTokenID: token,
        FAssetID: deviceId.toString()
      };

      let lastError = null;
      
      // Try each endpoint
      for (const [index, endpoint] of endpoints.entries()) {
        try {
          const response = await axios.post(endpoint, payload, {
            headers: {
              "Content-Type": "application/json",
              "X-Request-ID": requestId,
              "X-API-Version": "1.0"
            },
            timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || "5000")
          });

          // Handle successful response
          if (response.data?.Result === 200 && response.data.FObject?.length > 0) {
            const deviceData = response.data.FObject[0];
            setResponse({
              custom: true,
              requestId,
              code: "SUCCESS",
              message: "Device information retrieved successfully",
              action: "query_device",
              timestamp,
              data: {
                guid: deviceData.FGUID,
                assetId: deviceData.FAssetID,
                typeId: deviceData.FAssetTypeID,
                description: deviceData.FDescription,
                simNumber: deviceData.FSIMNumber,
                company: {
                  guid: deviceData.FAgentGUID,
                  name: deviceData.FAgentName
                },
                registrationTime: deviceData.FCreateTime,
                expirationTime: deviceData.FExpireTime,
                vehicle: deviceData.FVehicleGUID ? {
                  guid: deviceData.FVehicleGUID,
                  name: deviceData.FVehicleName
                } : null,
                lastUpdated: new Date().toISOString()
              },
              meta: {
                endpointUsed: endpoint,
                responseTime: response.headers['x-response-time']
              }
            });
            return;
          }

          // Handle API error codes
          const errorMappings = {
            102: { 
              code: "INVALID_PARAMETERS", 
              message: "Invalid parameters", 
              status: 400,
              action: "validate_request"
            },
            104: { 
              code: "INVALID_TOKEN", 
              message: "Token error or expired", 
              status: 401,
              action: "verify_token"
            },
            105: { 
              code: "PROCESSING_ERROR", 
              message: "Request processing error", 
              status: 400,
              action: "process_request"
            }
          };

          const errorInfo = errorMappings[response.data?.Result] || { 
            code: "API_ERROR",
            message: response.data?.Message || "API request failed", 
            status: 400,
            action: "call_api"
          };

          lastError = {
            ...errorInfo,
            responseData: response.data,
            endpoint
          };

        } catch (err) {
          console.error(`API request failed to ${endpoint}:`, err.message);
          lastError = {
            code: err.code || "API_CONNECTION_ERROR",
            message: err.message,
            status: err.response?.status || 500,
            action: "connect_endpoint",
            details: err.response?.data
          };
          
          if (index < endpoints.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
        }
      }

      // Handle failed requests
      throw {
        custom: true,
        requestId,
        code: lastError?.code || "INTERNAL_ERROR",
        message: lastError?.message || "Failed to fetch device information",
        action: lastError?.action || "process_request",
        timestamp,
        details: {
          errorCode: lastError?.responseData?.Result,
          attemptedEndpoints: endpoints,
          ...(process.env.NODE_ENV === 'development' && {
            debug: lastError?.details || lastError?.responseData
          })
        }
      };

    } catch (err) {
      console.error("Error in getDeviceInfo:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="device-info-container p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Device Information Query</h2>
      
      <div className="search-section mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="Enter Device ID"
            className="flex-1 p-2 border border-gray-300 rounded"
          />
          <button
            onClick={getDeviceInfo}
            disabled={isLoading}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            {isLoading ? 'Loading...' : 'Query'}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="loading-indicator p-4 bg-blue-50 rounded mb-4">
          <p>Fetching device information...</p>
        </div>
      )}

      {error && (
        <div className="error-message p-4 bg-red-50 border-l-4 border-red-500 rounded mb-4">
          <h3 className="font-bold text-red-700">{error.message}</h3>
          <div className="error-details mt-2 text-sm text-gray-700">
            <p><strong>Code:</strong> {error.code}</p>
            <p><strong>Action:</strong> {error.action}</p>
            <p><strong>Request ID:</strong> {error.requestId}</p>
            {error.details && (
              <div className="mt-2 p-2 bg-gray-100 rounded">
                <pre className="text-xs">{JSON.stringify(error.details, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {response && (
        <div className="response-container bg-white shadow rounded-lg overflow-hidden">
          <div className="response-header bg-green-50 p-4 border-b">
            <h3 className="font-bold text-green-800">{response.message}</h3>
            <div className="meta-info text-sm text-gray-600 mt-1">
              <span>Request ID: {response.requestId}</span>
              <span className="mx-2">•</span>
              <span>Endpoint: {response.meta.endpointUsed}</span>
            </div>
          </div>
          
          <div className="response-body p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="basic-info">
                <h4 className="font-semibold mb-2 text-gray-700">Basic Information</h4>
                <div className="space-y-2">
                  <p><strong>Asset ID:</strong> {response.data.assetId}</p>
                  <p><strong>Type ID:</strong> {response.data.typeId}</p>
                  <p><strong>Description:</strong> {response.data.description}</p>
                  <p><strong>SIM Number:</strong> {response.data.simNumber}</p>
                </div>
              </div>
              
              <div className="company-info">
                <h4 className="font-semibold mb-2 text-gray-700">Company Information</h4>
                <div className="space-y-2">
                  <p><strong>Company Name:</strong> {response.data.company.name}</p>
                  <p><strong>Company GUID:</strong> {response.data.company.guid}</p>
                </div>
              </div>
              
              <div className="timing-info">
                <h4 className="font-semibold mb-2 text-gray-700">Timing Information</h4>
                <div className="space-y-2">
                  <p><strong>Registration Time:</strong> {new Date(response.data.registrationTime).toLocaleString()}</p>
                  <p><strong>Expiration Time:</strong> {new Date(response.data.expirationTime).toLocaleString()}</p>
                </div>
              </div>
              
              {response.data.vehicle && (
                <div className="vehicle-info">
                  <h4 className="font-semibold mb-2 text-gray-700">Vehicle Information</h4>
                  <div className="space-y-2">
                    <p><strong>Vehicle Name:</strong> {response.data.vehicle.name}</p>
                    <p><strong>Vehicle GUID:</strong> {response.data.vehicle.guid}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="response-footer bg-gray-50 p-3 text-xs text-gray-500 border-t">
            <p>Last updated: {new Date(response.data.lastUpdated).toLocaleString()}</p>
            <p>Response time: {response.meta.responseTime || 'N/A'} ms</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceInfo;